import React, { useState, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  CheckCircle,
  XCircle,
  DownloadSimple,
  MapPin,
  Shapes,
  Camera,
  Smiley,
  SmileyMeh,
  SmileySad,
} from '@phosphor-icons/react'

import Badge from '@/components/Badge/Badge'

import PicturesModal from '../../Modal/PicturesModal'
import ConfirmPopup from '../../Popups/ConfirmPopup'
import { selectActiveBuilding } from '../../../reducers/buildingSelectorReducer'
import { INSPECTION_STATUS } from '../helpers'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import API from '../../../api/api'
import pdfDownload from '../../../helpers/pdfDownload'

import ImagesItem from './components/ImagesItem'
import LocationItem from './components/LocationItem'
import InspectionDataItem from './components/InspectionDataItem'

const calculateInspectionSummary = (inspection) => {
  if (!inspection || !inspection.rooms) return
  return {
    totalLocations: inspection.rooms.length,
    totalObjects: inspection.rooms.reduce((acc, room) => acc + (room.roomItems.length || 0), 0),
    conditions: {
      good: inspection.rooms.reduce(
        (acc, room) => acc + (room.roomItems.filter((item) => item.rating === 'Good').length || 0),
        0,
      ),
      fair: inspection.rooms.reduce(
        (acc, room) => acc + (room.roomItems.filter((item) => item.rating === 'Fair').length || 0),
        0,
      ),
      poor: inspection.rooms.reduce(
        (acc, room) => acc + (room.roomItems.filter((item) => item.rating === 'Poor').length || 0),
        0,
      ),
    },
    totalPhotos:
      inspection.rooms.reduce((totalCount, room) => {
        const roomImagesCount = room.roomItems.reduce((roomCount, item) => {
          return roomCount + (Array.isArray(item.images) ? item.images.length : 0)
        }, 0)

        return totalCount + roomImagesCount
      }, 0) + (inspection.note ? inspection.photos.length : 0),
  }
}

const InternalInspectionsDetails = ({ building }) => {
  const { t } = useTranslation(['internal-inspections', 'common'])
  const [inspection, setInspection] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [isWarningPopupOpen, setIsWarningPopupOpen] = useState(false)
  const [picturesToShow, setPicturesToShow] = useState({ data: null, index: 0 })

  const { displaySnackbar } = useSnackbar()
  const { inspectionId } = useParams()

  const fetchInspection = useCallback(async () => {
    setIsLoading(true)

    try {
      const { data } = await API.getInspectionById(inspectionId)
      setInspection(data)
      if (data.rooms && data.rooms.length) {
        setSelectedLocation(data.rooms[0])
      }
    } catch {
      displaySnackbar(t('internal-inspections:details.errorFetching'), true)
    } finally {
      setIsLoading(false)
    }
  }, [inspectionId, displaySnackbar])

  const markAsReviewed = useCallback(async () => {
    setIsLoading(true)
    try {
      const { data } = await API.updateScheduledInspection(inspectionId, {
        reviewRequired: false,
        reviewed: true,
      })
      setInspection(data)
      displaySnackbar(t('internal-inspections:details.approvedSuccessfully'))
    } catch {
      displaySnackbar(t('internal-inspections:details.errorApproving'), true)
    } finally {
      setIsWarningPopupOpen(false)
      setIsLoading(false)
    }
  }, [inspectionId, displaySnackbar])

  useEffect(() => {
    if (building && inspectionId) {
      fetchInspection()
    }
  }, [building, inspectionId, fetchInspection])

  const downloadPdfReport = async () => {
    try {
      setIsLoading(true)
      const res = await API.getInspectionPdfReport(inspectionId)
      pdfDownload(res, inspection.name)
      setIsLoading(false)
    } catch {
      displaySnackbar(t('internal-inspections:details.errorDownloadingPdf'), true)
      setIsLoading(false)
    }
  }
  const inspectionSummary = calculateInspectionSummary(inspection)

  return (
    <div className={`inspection-details ${isLoading ? 'is-loading' : ''}`}>
      {inspection ? (
        <React.Fragment>
          <div className="ctp-internal-inspections__view">
            <div className="ctp-internal-inspections__view__content">
              <div className="inspection-details-head">
                <div>
                  <span className="inspections-title">{inspection.name}</span>
                </div>
                <div>
                  <InspectionDataItem inspection={inspection} />
                  {inspection.reviewRequired &&
                    inspection.status === INSPECTION_STATUS.COMPLETED && (
                      <button
                        onClick={() => setIsWarningPopupOpen(true)}
                        className="button button-black"
                      >
                        {t('internal-inspections:details.markAsReviewed')}
                      </button>
                    )}
                  {inspection.status === INSPECTION_STATUS.COMPLETED && (
                    <button onClick={downloadPdfReport} className="button button-black">
                      {t('internal-inspections:details.downloadPdf')}
                      <DownloadSimple size={20} />
                    </button>
                  )}
                </div>
              </div>

              {inspection.workOrderIds && inspection.workOrderIds.length ? (
                <div className="work-order-links">
                  {inspection.workOrderIds.map((id) => (
                    <span key={id}>
                      <CheckCircle />
                      {t('internal-inspections:details.workOrderCreatedPrefix')}
                      <a
                        href={`#/dashboard/building-dashboard/maintenance/requests/${inspection.buildingId}/${id}`}
                      >
                        #{id}
                      </a>
                      )
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
          {inspection.note && (
            <div className="ctp-internal-inspections__view">
              <div className="ctp-internal-inspections__view__content inspection-details-notes">
                <div>
                  <span className="details-label">
                    {t('internal-inspections:details.inspectionNote')}
                  </span>
                  <span className="details-desc">{inspection.note}</span>
                </div>
                {inspection.photos && inspection.photos.length > 0 && (
                  <ImagesItem
                    images={inspection.photos}
                    setPicturesToShow={setPicturesToShow}
                    previewCount={4}
                  />
                )}
              </div>
            </div>
          )}
          {inspectionSummary && (
            <div className="ctp-internal-inspections__view">
              <div className="ctp-internal-inspections__view__content inspection-details-summary">
                <div className="inspection-details-summary__item">
                  <div className="inspection-details-summary__item-title">
                    <MapPin size={24} />
                    {t('internal-inspections:details.locationsTotal')}
                  </div>
                  <div className="inspection-details-summary__item-value">
                    {inspectionSummary.totalLocations}
                  </div>
                </div>
                <div className="inspection-details-summary__item">
                  <div className="inspection-details-summary__item-title">
                    <Shapes size={24} />
                    {t('internal-inspections:details.objectsTotal')}
                  </div>
                  <div className="inspection-details-summary__item-value">
                    {inspectionSummary.totalObjects}
                  </div>
                  <div className="inspection-details-summary__item-conditions">
                    {[
                      {
                        label: t('internal-inspections:details.ratingGood'),
                        value: inspectionSummary.conditions.good,
                        icon: <Smiley size={16} />,
                      },
                      {
                        label: t('internal-inspections:details.ratingFair'),
                        value: inspectionSummary.conditions.fair,
                        icon: <SmileyMeh size={16} />,
                      },
                      {
                        label: t('internal-inspections:details.ratingPoor'),
                        value: inspectionSummary.conditions.poor,
                        icon: <SmileySad size={16} />,
                      },
                    ].map((item) => (
                      <div
                        className="inspection-details-summary__item-conditions__item"
                        key={item.label}
                      >
                        <div className="inspection-details-summary__item-conditions__item-icon">
                          {item.icon}
                        </div>
                        <div className="inspection-details-summary__item-conditions__item-label">
                          {item.label}
                        </div>
                        <div className="inspection-details-summary__item-conditions__item-value">
                          {item.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="inspection-details-summary__item">
                  <div className="inspection-details-summary__item-title">
                    <Camera size={24} />
                    {t('internal-inspections:details.photosTotal')}
                  </div>
                  <div className="inspection-details-summary__item-value">
                    {inspectionSummary.totalPhotos}
                  </div>
                </div>
              </div>
            </div>
          )}
          {inspection.rooms && inspection.rooms.length > 0 && (
            <div className="ctp-internal-inspections__view">
              <div className="ctp-internal-inspections__view__content inspection-details-locations">
                <div className="inspection-details-locations__sidebar">
                  {inspection.rooms.map((room) => {
                    const poorRoomItems = room.roomItems.filter((item) => item.rating === 'Poor')
                    const hasIssueRoomItems = room.roomItems.filter((item) => item.hasIssue)
                    const showPoorBadge = poorRoomItems.length > 0 || hasIssueRoomItems.length > 0

                    return (
                      <div
                        className={`sidebar-item ${
                          selectedLocation && selectedLocation.id === room.id ? 'selected' : ''
                        } ${room.isCheckedByUser ? 'success' : 'error'}`}
                        key={room.id}
                        onClick={() => setSelectedLocation(room)}
                        type="button"
                      >
                        <span>{room.name}</span>
                        {showPoorBadge && (
                          <Badge color="red-filled">
                            {t('internal-inspections:details.poorCount', {
                              count: poorRoomItems.length || hasIssueRoomItems.length,
                            })}
                          </Badge>
                        )}
                        {room.isCheckedByUser ? <CheckCircle /> : <XCircle />}
                      </div>
                    )
                  })}
                </div>
                {selectedLocation && (
                  <div className="inspection-details-locations__body">
                    <span className="inspections-title">{selectedLocation.name}</span>
                    {selectedLocation.roomItems &&
                      selectedLocation.roomItems.map((item) => (
                        <LocationItem
                          key={item.id}
                          item={item}
                          isGuided={inspection.guided}
                          setPicturesToShow={setPicturesToShow}
                          inspectionId={inspectionId}
                          location={selectedLocation}
                          refetchDetails={fetchInspection}
                        />
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </React.Fragment>
      ) : null}
      <PicturesModal
        assetPath="inspection_photos"
        visible={!!picturesToShow.data}
        pictures={picturesToShow.data || []}
        indexToShow={picturesToShow.index}
        onCloseModal={() => setPicturesToShow({ data: null, index: 0 })}
      />
      <ConfirmPopup
        isOpen={isWarningPopupOpen}
        cancelCallback={() => setIsWarningPopupOpen(false)}
        confirmCallback={markAsReviewed}
        isLoading={isLoading}
        cancelText={t('common:actions.back')}
        confirmText={t('internal-inspections:details.markAsReviewed')}
        title={t('internal-inspections:dashboard.cautionTitle')}
        body={t('internal-inspections:details.confirmMarkReviewedBody')}
        className="is-caution"
      />
    </div>
  )
}

InternalInspectionsDetails.propTypes = {
  building: PropTypes.object,
}

export default connect(
  (state) => ({
    building: selectActiveBuilding(state),
  }),
  null,
)(InternalInspectionsDetails)
